import axios, { AxiosError, CreateAxiosDefaults, InternalAxiosRequestConfig } from "axios";
import { msal } from "./msal";
import { Page, ServiceClient } from "@/util";
import { Token } from "@/types";

const options: CreateAxiosDefaults = {
  baseURL: "/api/",
  withCredentials: true,
};

const instance = axios.create(options);
const instanceWithoutInterceptors = axios.create(options);

instance.interceptors.request.use(requestInterceptor);
instance.interceptors.response.use((x) => x, errorInterceptor);

async function requestInterceptor(config: InternalAxiosRequestConfig) {
  const instance = msal.getInstance();

  if (instance) {
    // Azure login
    const token = await msal.getAccessToken(instance);
    config.headers.authorization = `bearer ${token.accessToken}`;
  } else {
    // Basic login
    const token = ServiceClient.getToken();
    config.headers.authorization = `bearer ${token.accessToken}`;
  }

  return config;
}

async function errorInterceptor(error: AxiosError) {
  const status = error.response ? error.response.status : null;
  if (status !== 401) {
    return Promise.reject(error);
  }

  const token = ServiceClient.getToken();
  if (!token.userIds || !token.refreshToken) {
    window.location.href = window.location.origin;
    return Promise.reject(error);
  }

  if (await reauthenticate(token, 1)) {
    const originalRequest = error.config;
    if (originalRequest) {
      return instance.request(originalRequest);
    }
  }
}

function reauthenticate(token: Token, attempts: number) {
  const payLoad = {
    userIds: token.userIds,
    refreshToken: token.refreshToken,
    grantType: "RefreshToken",
  };

  return authenticate(payLoad)
    .then((response) => {
      ServiceClient.setToken(response.data);
      return true;
    })
    .catch(() => {
      if (attempts > 2) {
        ServiceClient.clearToken();
        Page.redirect();
      } else {
        attempts++;
        reauthenticate(token, attempts);
      }
    });
}

function authenticate(payload?: object) {
  const token = ServiceClient.getToken();

  let headers = {};
  if (token.accessToken) {
    headers = { Authorization: `Bearer ${token.accessToken}` };
  }

  return instanceWithoutInterceptors.post<Token>("tokens", payload, headers);
}

export { instance, authenticate };
